/**
 * features/control/hooks.ts — TanStack Query read hooks for the Kontrollbereich Cockpit
 * (Bereich 5). Each is the `useCarrierRows` live/mock split: live → the typed api client;
 * mock → a deterministic fixture with `initialData` + `staleTime: Infinity` so the panel /
 * list / detail render standalone in tests and dev.
 *
 * Mutations (zuweisen, setFristen) live inline in the components (useMutation + qk
 * invalidation) — they ALWAYS call the api client (tests mock the client or rely on mock mode).
 *
 * [Source: apps/web/src/features/administration/hooks.ts] (the shared live/mock split)
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { apiClient } from '../../lib/api-client';
import type {
  KontrolleFristenDto,
  KontrolleRueckstandDetailDto,
  KontrolleRueckstandItemDto,
  KontrolleUebersichtDto,
} from './types';
import {
  MOCK_UEBERSICHT,
  MOCK_FRISTEN,
  mockRueckstandForStufe,
  mockRueckstandDetail,
} from './mock';

/** Generic live/mock read (the useAdminRead shape). */
function useControlRead<T>(
  queryKey: readonly unknown[],
  liveFn: () => Promise<T>,
  fixture: T,
  opts: { enabled?: boolean } = {},
): UseQueryResult<T> {
  const live = isLiveData();
  return useQuery<T>({
    queryKey,
    queryFn: () => (live ? liveFn() : Promise.resolve(fixture)),
    enabled: opts.enabled ?? true,
    ...(live ? {} : { initialData: fixture, staleTime: Infinity }),
  });
}

/** GET /admin/kontrolle/uebersicht — the dashboard-panel aggregate. */
export const useKontrolleUebersicht = (
  enabled = true,
): UseQueryResult<KontrolleUebersichtDto> =>
  useControlRead(
    qk.kontrolleUebersicht(),
    () => apiClient.admin.kontrolleUebersicht(),
    MOCK_UEBERSICHT,
    { enabled },
  );

/** The Kontrollliste filter set (Kanal/Kunde client-side in mock; Team server-side). */
export interface KontrolleListeFilters {
  kanal?: string;
  kunde?: string;
  team?: string;
}

/** Apply the cheap client-side filters (kanal + kunde) to a fixture list (mock mode). */
function applyMockFilters(
  rows: KontrolleRueckstandItemDto[],
  filters: KontrolleListeFilters,
): KontrolleRueckstandItemDto[] {
  const kunde = filters.kunde?.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!filters.kanal || r.kanal === filters.kanal) &&
      (!kunde || (r.kunde_name ?? '').toLowerCase().includes(kunde)),
  );
}

/**
 * GET /admin/kontrolle/rueckstand?stufe=N — the per-stage Kontrollliste. Filters are passed
 * to the API server-side in live mode; in mock mode the cheap kanal + kunde filters are
 * applied to the fixture (team is a server-side scope, ignored by the mock).
 */
export const useKontrolleRueckstand = (
  stufe: number,
  filters: KontrolleListeFilters = {},
  enabled = true,
): UseQueryResult<KontrolleRueckstandItemDto[]> =>
  useControlRead(
    [...qk.kontrolleRueckstandStufe(stufe), filters],
    () => apiClient.admin.kontrolleRueckstand(stufe, filters),
    applyMockFilters(mockRueckstandForStufe(stufe), filters),
    { enabled },
  );

/** GET /admin/kontrolle/rueckstand/:id — one element's detail. */
export const useKontrolleDetail = (
  id: string,
  enabled = true,
): UseQueryResult<KontrolleRueckstandDetailDto> =>
  useControlRead(
    qk.kontrolleRueckstandDetail(id),
    () => apiClient.admin.kontrolleRueckstandDetail(id),
    mockRueckstandDetail(id),
    { enabled },
  );

/** GET /admin/einstellungen/kontrolle-fristen — the per-broker stage thresholds. */
export const useKontrolleFristen = (
  enabled = true,
): UseQueryResult<KontrolleFristenDto> =>
  useControlRead(
    qk.kontrolleFristen(),
    () => apiClient.admin.kontrolleFristen(),
    MOCK_FRISTEN,
    { enabled },
  );
